import BankLogo from '@/components/icons/BankLogo';
import { useSentry } from '@/hooks/useSentry';
import { SVSelectProps } from '@/types/common';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { camelCase } from '@/utils/stringCaser';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: '80vh',
    },
  },
};

export default function SVSelect({
  name,
  disabled = false,
  label,
  options = [],
  rules,
  inputProps,
  breadcrumbLabel,
}: SVSelectProps) {
  const methods = useFormContext();
  const builtRulesObject = buildInputRulesObject(label, rules);
  const { addUserActionBreadcrumb } = useSentry();

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={builtRulesObject}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { error },
      }) => (
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            fullWidth
            select
            id={camelCase(label)}
            label={label}
            value={value ?? ''}
            onChange={e => {
              if (breadcrumbLabel) {
                addUserActionBreadcrumb(
                  `Selected from ${breadcrumbLabel}`,
                  'ui.select',
                );
              }
              onChange(e);
            }}
            onBlur={onBlur}
            inputRef={ref}
            error={!!error?.message}
            disabled={disabled}
            slotProps={{
              select: { MenuProps },
              input: inputProps,
            }}
            helperText={error?.message}>
            {options.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    flexWrap: 'wrap',
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexGrow: 1,
                      textWrap: 'pretty',
                    }}>
                    {option.icon && (
                      <option.icon
                        color="primary"
                        height="35"
                        width="35"
                        style={{ marginRight: '8px' }}
                      />
                    )}
                    {option.iconUrl && (
                      <BankLogo
                        src={option.iconUrl}
                        style={{ marginRight: '8px', marginLeft: '-4px' }}
                      />
                    )}
                    {option.label}
                  </Box>

                  {option.additionalInfo && (
                    <Typography sx={{ ml: 2 }}>
                      {option.additionalInfo}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    />
  );
}
